import { store } from 'polaris-coreweb/exports';

/**
 * Get ad config.
 * adConfig is set to the context in BaseComponent (DE siteapp) at contextOverride(...)
 * @param {Object} props.adUnitPath - Ad unit path
 * @param {Object} props.adConfig - Ad config
 * @param {Object} props.localTargeting - Local targeting
 * @param {string} props.type - Ad type
 * @param {Object} props.slotSize - Slot size
 * @param {Object} props.sizeMapping - Size mapping
 * @returns {Object} - Ad config
 */
export const getAdConfig = (props) => {
  const {
    adUnitPath,
    adConfig,
    localTargeting,
    type,
    slotSize,
    sizeMapping,
  } = props;

  // Removes false-y values from an object.
  const removeFalseyValues = obj => (
    Object.entries(obj)
      .filter(([, val]) => (!Array.isArray(val) && val) || (Array.isArray(val) && val.length))
      .reduce((prev, [key, val]) => ({ ...prev, [key]: val }), {})
  );

  // Functionality to get if is object.
  const isObject = (obj) => obj === Object(obj);

  // Get type config from templates, if any.
  const { templates, ...globalAdConfig } = adConfig;
  let typeConfig = {};
  if (type && isObject(templates) && isObject(templates[type])) {
    typeConfig = templates[type];
  }

  // Deep merge targeting.
  const targeting = [
    adConfig.targeting,
    typeConfig.targeting,
    localTargeting,
    adConfig.globalTargeting,
  ].filter(target => isObject(target)).reduce((acc, cur) => {
    if (isObject(cur)) {
      return { ...acc, ...cur };
    }
    return acc;
  }, {});

  // Remove falsey values from local.
  const local = removeFalseyValues({
    adUnitPath,
    slotSize,
    sizeMapping,
  });

  return {
    ...globalAdConfig,
    ...typeConfig,
    ...local,
    targeting,
  };
};

// Get the current viewport width and height.
export const getViewportDimensions = () => {
  const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  return [vw, vh];
};

/**
 * Get active slot sizes based on the viewport width and the size mapping.
 * @param {Array} slotSize
 * @param {Object} sizeMapping
 * @returns
 */
export const getActiveSizeMapping = (slotSize, sizeMapping) => {
  if (!Array.isArray(sizeMapping) || sizeMapping.length === 0) {
    return slotSize;
  }
  const [vw] = getViewportDimensions();
  const activeSizeMapping = sizeMapping.slice(0).reverse().find(({ viewport }) => viewport[0] <= vw);
  return activeSizeMapping ? activeSizeMapping.slot : slotSize;
};

/**
 * Get the maximum height to reserve for the slot.
 * @param {Array} slotSize
 * @param {Object} sizeMapping
 * @returns {string} - The height to reserve for the slot
 */
export const getReservedHeight = (slotSize, sizeMapping) => {
  // Check if both slotSize and sizeMapping are arrays, if not, default to an empty array
  const checkedSlotSize = Array.isArray(slotSize) ? slotSize : [];
  const checkedSizeMapping = Array.isArray(sizeMapping) ? sizeMapping : [];

  // Combine all possible slot sizes into one array.
  const allSizes = [
    ...checkedSlotSize,
    ...checkedSizeMapping.flatMap(({ slot }) => slot),
  ];

  // Find the maximum height.
  const maxHeight = allSizes
    .filter(Array.isArray)
    .reduce((max, size) => Math.max(max, size[1]), 0);

  return maxHeight || 'unset';
};

/**
 * Validate the sizes.
 * @param {Object} sizes
 * @returns {boolean} - true if the sizes are valid
 */
const sizesValid = sizes => sizes.find((s) => {
  if (Array.isArray(s)) return sizesValid(s);
  if (typeof s === 'string') return false;
  return !(typeof s === 'number' && s <= 2);
});

/**
 * Check if the lineItemId is blacklisted.
 * @param {Object} slot - the ad slot
 * @param {Array} lineItemBlacklist - array of blacklisted line item ids
 * @return {boolean} true if the lineItemId is blacklisted
 */
export const isLineItemIdBlacklisted = (slot, lineItemBlacklist) => {
  if (slot && lineItemBlacklist?.length) {
    const latestSlotResponseInfo = slot?.getResponseInformation && slot.getResponseInformation();
    const lineItemId = latestSlotResponseInfo?.lineItemId?.toString();
    if (lineItemId) {
      return lineItemBlacklist.includes(lineItemId);
    }
  }
  return false;
};

/**
 * Check if the slot is defined.
 * @param {string} id - slot id
 * @return {boolean} true if the slot is defined
 */
export const isSlotDefined = (id) => {
  if (typeof window !== 'undefined' && window.googletag && window.googletag.apiReady) {
    const { googletag } = window;
    return googletag.pubads().getSlots().some(slot => slot.getSlotElementId() === id);
  }
  return false;
};

/**
 * Get the slot.
 * @param {string} id - slot id
 * @return {Object} the slot
 */
export const getSlot = (id) => {
  if (typeof window !== 'undefined' && window.googletag && window.googletag.apiReady) {
    const { googletag } = window;
    return googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === id);
  }
  return null;
};

/**
 * Destroy the ad slot.
 * @param {string} id - slot id
 * @return {boolean} true if the slot is destroyed
 */
export const destroySlot = (id) => {
  if (typeof window !== 'undefined' && window.googletag && window.googletag.apiReady) {
    const { googletag } = window;
    const slot = googletag.pubads().getSlots().find(s => s.getSlotElementId() === id);
    if (slot) {
      googletag.destroySlots([slot]);
      return true;
    }
  }
  return false;
};

/**
 * Fetch bids function.
 * @param {string} id - slot id
 * @param {function} callback - callback function, usually pubads.refresh.
 * @param {Object} size - slot size
 * @param {Object} mapping - size mapping
 * @param {Object} headerBidding - header bidding config
 * @return {undefined}
 */
export const fetchBids = ({
  slotId, displaySlot, size, mapping, headerBidding,
}) => {
  const {
    amazonHeaderBiddingEnabled,
    nonBiddableSlots,
  } = headerBidding || {};

  // NOTE: headerBidding.enabled is currently set to false in the portal. Should we be using this?
  if (!nonBiddableSlots?.includes(slotId)) {
    const arrDone = [];
    const biddersReady = (name) => {
      if (arrDone.includes(name)) return;
      arrDone.push(name);
      if (arrDone.length > 1) {
        displaySlot();
      }
    };

    if (
      typeof window !== 'undefined'
      && window.googletag
      && window.googletag.apiReady
      && typeof window.ozoneFetchBids === 'function'
    ) {
      // Ozone only.
      if (!amazonHeaderBiddingEnabled) {
        window.ozoneFetchBids([slotId], () => biddersReady('ozone'));
      }

      // Amazon and Ozone header bidding.
      if (window.apstagLOADED && amazonHeaderBiddingEnabled) {
        // Start the ozone bidding.
        window.ozoneFetchBids(slotId, () => biddersReady('ozone'));

        const sizes = getActiveSizeMapping(size, mapping);
        if (!sizesValid(sizes)) return displaySlot();

        const apstagCallback = () => {
          window.googletag.cmd.push(() => {
            window.apstag.setDisplayBids();
            biddersReady('amazon');
          });
        };

        // Start the Amazon bidding.
        return window.apstag.fetchBids({
          slots: [{
            slotID: slotId,
            slotName: getSlot(slotId)?.getAdUnitPath(),
            sizes,
          }],
        }, apstagCallback);
      }

      // If either amazon or ozone is not ready, just display the slot.
      setTimeout(() => {
        biddersReady('amazon');
        biddersReady('ozone');
      }, 4000);
    }
  } else {
    displaySlot();
  }
  return undefined;
};

/**
 * Refresh the ad slot.
 * @param {Object} slot - ad slot to be refreshed
 */
export const refreshSlot = (slot, slotSize, sizeMapping, headerBidding) => {
  if (typeof window !== 'undefined' && window.googletag && window.googletag.apiReady) {
    const { googletag } = window;
    const slotId = slot.getSlotElementId();
    if (slot && googletag) {
      fetchBids({
        slotId,
        displaySlot: () => googletag.pubads().refresh([slot]),
        size: slotSize,
        mapping: sizeMapping,
        headerBidding,
      });
    }
  }
};

/**
 * Set the page level targeting for the ad slot.
 * @param {Object} targeting - targeting key value pairs
 */
export const setPageTargeting = (targeting = {}) => {
  if (targeting.length) {
    if (typeof window !== 'undefined' && window.googletag && window.googletag.apiReady) {
      const { googletag } = window;
      Object.keys(targeting).forEach((key) => {
        googletag.pubads().setTargeting(key, targeting[key]);
      });
    }
  }
};

/**
 * Create the ad slot.
 * @param {Object} props - ad slot properties
 */
export const createSlot = (props) => {
  const {
    id,
    mergedConfig: config,
    firstRun,
    collapseEmptyDiv,
    breakpoints,
    outOfPage,
    headerBidding,
    immediateLoadAdSlots,
    onSlotRenderEnded,
    onSlotLoad,
    onSlotRequested,
    onSlotVisibilityChanged,
    onSlotResponseReceived,
    onImpressionViewable,
    withIas,
  } = props;

  const {
    adUnitPath,
    slotSize,
    sizeMapping,
    targeting,
  } = config;

  if (!adUnitPath) {
    return;
  }
  window.googletag = window.googletag || { cmd: [] };
  const { googletag } = window;
  googletag.cmd.push(() => {
    // Check if the slot is defined before anything.
    if (googletag.pubads().getSlots().some(s => s.getSlotElementId() === id)) {
      return;
    }

    let slot = {};
    if (outOfPage) {
      slot = googletag.defineOutOfPageSlot(adUnitPath, id);
    } else {
      slot = googletag.defineSlot(adUnitPath, slotSize, id);
    }
    slot.addService(googletag.pubads());
    slot.setCollapseEmptyDiv(collapseEmptyDiv);

    googletag.pubads().setCentering(true);

    // Add the size mapping for the slot if it is responsive.
    if (sizeMapping) {
      const mapping = googletag.sizeMapping();
      sizeMapping.forEach((sizeMap) => {
        mapping.addSize(sizeMap.viewport, sizeMap.slot);
      });
      slot.defineSizeMapping(mapping.build());

      // Sometimes we will have a desktop size ad when *technically* on mobile width.
      // So we also need to add the breakpoints from config as well as the sizeMapping.
      // TODO - maybe add some matchMedia validation.
      const adResponsiveBreakpoints = [];
      sizeMapping.forEach((sizeMap) => {
        adResponsiveBreakpoints.push(window.matchMedia(`(min-width: ${sizeMap.viewport[0]}px)`));
      });

      if (breakpoints?.length) {
        Object.values(breakpoints).forEach((breakpoint) => {
          const mediaQueryList = window.matchMedia(breakpoint);
          if (!adResponsiveBreakpoints.includes(mediaQueryList)) {
            adResponsiveBreakpoints.push(mediaQueryList);
          }
        });
      }

      // Refresh the slot when the breakpoint changes.
      adResponsiveBreakpoints.forEach((breakpoint) => {
        breakpoint.addEventListener('change', () => {
          refreshSlot(slot, slotSize, sizeMapping, headerBidding);
        });
      });
    }

    // Set the slot targeting.
    if (targeting && slot) {
      Object.keys(targeting).forEach((key) => {
        slot.setTargeting(key, targeting[key]);
      });
    }

    // Set the callback functions - we only want to do this once.
    // https://developers.google.com/publisher-tag/samples/ad-event-listeners
    if (firstRun) {
      googletag.pubads().addEventListener('slotRenderEnded', onSlotRenderEnded);
      googletag.pubads().addEventListener('slotOnload', onSlotLoad);
      googletag.pubads().addEventListener('slotRequested', onSlotRequested);
      googletag.pubads().addEventListener('slotVisibilityChanged', onSlotVisibilityChanged);
      googletag.pubads().addEventListener('slotResponseReceived', onSlotResponseReceived);
      googletag.pubads().addEventListener('impressionViewable', onImpressionViewable);

      // Disable initial load so we can control exactly when we want to fetch the slots.
      googletag.pubads().disableInitialLoad();

      // Enable services.
      googletag.enableServices();
    }

    googletag.display(id);

    // Get the immediate load ad slots from config.
    const biddableSlots = immediateLoadAdSlots?.biddableSlots || [];
    const nonBiddableSlots = immediateLoadAdSlots?.nonBiddableSlots || [];

    let initialFetched = false;
    let displayed = false;

    // Fetch the bids with or without the withIas function.
    const fetchBidsAndDisplay = (withIasOptions) => {
      const fetchBidsOptions = {
        slotId: id,
        displaySlot: () => googletag.pubads().refresh([slot]),
        size: slotSize,
        mapping: sizeMapping,
        headerBidding,
      };
      if (withIasOptions) {
        withIas({
          display: () => fetchBids(fetchBidsOptions),
          ...withIasOptions,
        });
      } else {
        fetchBids(fetchBidsOptions);
      }
      initialFetched = true;
      displayed = true;
    };

    // If the current slot is in the initial slots array, fetch the bids/request and refresh the slot now.
    if (nonBiddableSlots.includes(id)) {
      fetchBidsAndDisplay();
    } else if (biddableSlots.includes(id)) {
      fetchBidsAndDisplay({ iasPetTimeout: 2000 });
    } else {
      googletag.pubads().enableLazyLoad({
        renderMarginPercent: 10,
        mobileScaling: 2.0,
      });
    }

    // Set the slot to defined in the store.
    store.setState((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        defined: true,
        initialFetched,
        displayed,
      },
    }));
  });
};
